/**
 * Whether a fetch outcome means the network is gone.
 *
 * Lives apart from `network-error-dialog` because it is the decision, not the
 * dialog: these are pure predicates over a fetch result, the only part of that
 * component worth testing directly, and exporting them from a file that also
 * exports a component costs it Fast Refresh.
 */

/**
 * Is this fetch rejection a genuine loss of connectivity?
 *
 * An `AbortError` is the app cancelling its own request — a conversation
 * switched, a component unmounted, a search superseded — and treating it as a
 * dropped connection is why Presence flashed "network lost" on a perfectly good
 * network. It aborts in-flight loads constantly, by design.
 *
 * Everything else that `fetch` rejects with genuinely means the request never
 * completed, so it still counts.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false
  // Not every runtime rejects with a DOMException; the name is the reliable part.
  if (typeof error === 'object' && error !== null && 'name' in error) {
    if ((error as { name?: unknown }).name === 'AbortError') return false
  }
  return true
}

/**
 * Should this fetch outcome black the app out with "Connection Lost"?
 *
 * A RESPONSE never should, whatever its status. The overlay tells the user to
 * check their Wi-Fi, move closer to the router and call their ISP — advice that
 * is actively misleading when the server answered, because the connection
 * plainly works. A 500 from one endpoint was enough to take the whole app down
 * behind that screen: uploading a chat attachment to a backend with no storage
 * credentials failed, and the user was told to restart their router.
 *
 * Only a request that never completed counts, and even then not one the app
 * cancelled itself.
 */
export function isConnectionLost(
  outcome: { kind: 'response'; status: number } | { kind: 'rejected'; error: unknown },
): boolean {
  // The server answered. Whatever went wrong is the server's, not the network's,
  // and the endpoint that made the call is the only thing that can explain it.
  if (outcome.kind === 'response') return false
  return isNetworkFailure(outcome.error)
}

/**
 * Is the network actually reachable right now? What "Try Again" asks.
 *
 * `navigator.onLine` alone cannot answer it: true only means some interface is
 * up — Wi-Fi joined to a router with no uplink, a VPN or Docker adapter — so
 * Try Again closed "Connection Lost" over a dashboard that still could not load,
 * and the user was left with no sign the network was the problem. False is
 * trustworthy, so it short-circuits; true has to be proved by a request
 * completing. Any response counts, whatever its status (see isConnectionLost).
 */
export async function canReachNetwork(
  online: boolean,
  probe: () => Promise<unknown>,
): Promise<boolean> {
  if (!online) return false
  try {
    await probe()
    return true
  } catch {
    return false
  }
}
